/**
 * Mapping of navigation paths to permission names
 * Based on the permission names from the API response
 */
const NAV_PERMISSION_MAP = {
  "/dashboard": "Dashboard",
  "/user-management": "user Management",
  "/courses": "Course",
  "/cultural-module": "Cultural",
  "/roles": "Roles & Permission",
  "/permissions": "Roles & Permission",
  "/sub-admin": "Roles & Permission",
  "/category": "category & region",
  "/region": "category & region",
  "/privacy-policy": "Terms & Privacy",
  "/terms-and-conditions": "Terms & Privacy",
};

/**
 * Normalizes permission name for comparison (case-insensitive, trim whitespace)
 * @param {string} name - Permission name to normalize
 * @returns {string} Normalized permission name
 */
const normalizePermissionName = (name) => {
  if (!name || typeof name !== "string") return "";
  return name.trim().toLowerCase();
};

/**
 * Checks if user has read permission for a specific permission name
 * @param {Array} permissions - Array of permission objects from roleInfo.permissions
 * @param {string} permissionName - Permission name to check (case-insensitive)
 * @returns {boolean} True if user has read permission, false otherwise
 */
const hasReadPermissionByName = (permissions, permissionName, userType) => {
  // Admin bypass
  if (userType === "admin" || userType === "super-admin") {
    return true;
  }

  if (!permissions || !Array.isArray(permissions) || !permissionName) {
    return false;
  }
  const normalizedName = normalizePermissionName(permissionName);
  const permission = permissions.find((p) => {
    const permName = normalizePermissionName(p.name);
    return permName === normalizedName;
  });
  return permission?.read === true;
};

/**
 * Filters navigation items based on user permissions
 * @param {Array} navItems - Array of navigation items to filter
 * @param {string} userType - User type (admin, sub-admin, etc.)
 * @param {Array} permissions - Array of permission objects from roleInfo.permissions
 * @returns {Array} Filtered navigation items
 */
export const getAccessibleNavItems = (
  navItems,
  userType,
  permissions
) => {
  // If user is super-admin (or no user_type specified), show all items
  if (!userType || userType === "admin" || userType === "super-admin") {
    return navItems;
  }

  // For sub-admin users, filter based on permissions
  if (userType === "sub-admin") {
    // First pass: filter items based on permissions
    const filteredItems = navItems.filter((item) => {
      // Section titles - we'll handle these in the second pass
      if (item.sectionTitle) {
        return true;
      }

      // If item has no path, hide it
      if (!item.path) {
        return false;
      }

      // Check if this path requires a permission
      const requiredPermissionName = NAV_PERMISSION_MAP[item.path];

      // If no permission mapping exists, hide the item (only show items with explicit permissions)
      if (!requiredPermissionName) {
        return false;
      }

      // Check if user has read permission for this path
      return hasReadPermissionByName(permissions, requiredPermissionName);
    });

    // Second pass: filter out section titles that have no visible items below them
    const result = [];
    for (let i = 0; i < filteredItems.length; i++) {
      const item = filteredItems[i];

      // If it's a section title, check if there are any visible items after it
      if (item.sectionTitle) {
        // Look ahead to see if there are any non-section-title items before the next section title
        let hasVisibleItems = false;
        for (let j = i + 1; j < filteredItems.length; j++) {
          if (filteredItems[j].sectionTitle) {
            break; // Reached next section title
          }
          if (filteredItems[j].path) {
            hasVisibleItems = true;
            break;
          }
        }
        // Only include section title if there are visible items in this section
        if (hasVisibleItems) {
          result.push(item);
        }
      } else {
        // Regular item - always include if it passed the first filter
        result.push(item);
      }
    }

    return result;
  }

  // Default: show all items for other user types
  return navItems;
};

/**
 * Gets a permission object by permission name
 * @param {Array} permissions - Array of permission objects from roleInfo.permissions
 * @param {string} permissionName - Permission name to get (case-insensitive)
 * @returns {Object|null} Permission object or null if not found
 */
export const getPermissionByName = (permissions, permissionName) => {
  if (!permissions || !Array.isArray(permissions) || !permissionName) {
    return null;
  }
  const normalizedName = normalizePermissionName(permissionName);
  return permissions.find((p) => {
    const permName = normalizePermissionName(p.name);
    return permName === normalizedName;
  }) || null;
};

/**
 * Gets a permission object by permission ID
 * @param {Array} permissions - Array of permission objects from roleInfo.permissions
 * @param {string|number} permissionId - Permission ID to get
 * @returns {Object|null} Permission object or null if not found
 */
export const getPermissionById = (permissions, permissionId) => {
  if (!permissions || !Array.isArray(permissions) || permissionId === undefined || permissionId === null) {
    return null;
  }
  return permissions.find(
    (p) => p.permission_id === String(permissionId) || p.permission_id === permissionId
  ) || null;
};

/**
 * Checks if user has read permission for a specific permission name
 * @param {Array} permissions - Array of permission objects from roleInfo.permissions
 * @param {string} permissionName - Permission name to check (case-insensitive)
 * @returns {boolean} True if user has read permission, false otherwise
 */
export const hasPermission = (permissions, permissionName, userType) => {
  return hasReadPermissionByName(permissions, permissionName, userType);
};

/**
 * Checks if user has create permission for a specific permission name
 * @param {Array} permissions - Array of permission objects from roleInfo.permissions
 * @param {string} permissionName - Permission name to check (case-insensitive)
 * @returns {boolean} True if user has create permission, false otherwise
 */
export const hasCreatePermission = (permissions, permissionName, userType) => {
  if (userType === "admin" || userType === "super-admin") return true;

  if (!permissions || !Array.isArray(permissions) || !permissionName) {
    return false;
  }
  const permission = getPermissionByName(permissions, permissionName);
  return permission?.create === true;
};

/**
 * Checks if user has update permission for a specific permission name
 * @param {Array} permissions - Array of permission objects from roleInfo.permissions
 * @param {string} permissionName - Permission name to check (case-insensitive)
 * @returns {boolean} True if user has update permission, false otherwise
 */
export const hasUpdatePermission = (permissions, permissionName) => {
  if (!permissions || !Array.isArray(permissions) || !permissionName) {
    return false;
  }
  const permission = getPermissionByName(permissions, permissionName);
  return permission?.update === true;
};

/**
 * Checks if user has remove/delete permission for a specific permission name
 * @param {Array} permissions - Array of permission objects from roleInfo.permissions
 * @param {string} permissionName - Permission name to check (case-insensitive)
 * @returns {boolean} True if user has remove permission, false otherwise
 */
export const hasRemovePermission = (permissions, permissionName) => {
  if (!permissions || !Array.isArray(permissions) || !permissionName) {
    return false;
  }
  const permission = getPermissionByName(permissions, permissionName);
  return permission?.remove === true;
};

/**
 * Checks if user has create permission for a specific permission ID
 * @param {Array} permissions - Array of permission objects from roleInfo.permissions
 * @param {string|number} permissionId - Permission ID to check
 * @returns {boolean} True if user has create permission, false otherwise
 */
export const hasCreatePermissionById = (permissions, permissionId) => {
  if (!permissions || !Array.isArray(permissions) || permissionId === undefined || permissionId === null) {
    return false;
  }
  const permission = getPermissionById(permissions, permissionId);
  return permission?.create === true;
};

/**
 * Checks if user has update permission for a specific permission ID
 * @param {Array} permissions - Array of permission objects from roleInfo.permissions
 * @param {string|number} permissionId - Permission ID to check
 * @returns {boolean} True if user has update permission, false otherwise
 */
export const hasUpdatePermissionById = (permissions, permissionId) => {
  if (!permissions || !Array.isArray(permissions) || permissionId === undefined || permissionId === null) {
    return false;
  }
  const permission = getPermissionById(permissions, permissionId);
  return permission?.update === true;
};

/**
 * Checks if user has remove/delete permission for a specific permission ID
 * @param {Array} permissions - Array of permission objects from roleInfo.permissions
 * @param {string|number} permissionId - Permission ID to check
 * @returns {boolean} True if user has remove permission, false otherwise
 */
export const hasRemovePermissionById = (permissions, permissionId) => {
  if (!permissions || !Array.isArray(permissions) || permissionId === undefined || permissionId === null) {
    return false;
  }
  const permission = getPermissionById(permissions, permissionId);
  return permission?.remove === true;
};

/**
 * Gets permission object for a specific route/path
 * @param {Array} permissions - Array of permission objects from roleInfo.permissions
 * @param {string} path - Navigation path (e.g., "/courses", "/cultural-module")
 * @returns {Object|null} Permission object or null if not found
 */
export const getPermissionByPath = (permissions, path) => {
  if (!permissions || !Array.isArray(permissions) || !path) {
    return null;
  }
  const permissionName = NAV_PERMISSION_MAP[path];
  if (!permissionName) {
    return null;
  }
  return getPermissionByName(permissions, permissionName);
};

/**
 * Checks if user has create permission for a specific route/path
 * @param {Array} permissions - Array of permission objects from roleInfo.permissions
 * @param {string} path - Navigation path (e.g., "/courses", "/cultural-module")
 * @returns {boolean} True if user has create permission, false otherwise
 */
export const canCreate = (permissions, path, userType) => {
  if (userType === "admin" || userType === "super-admin") return true;
  const permission = getPermissionByPath(permissions, path);
  return permission?.create === true;
};

/**
 * Checks if user has update permission for a specific route/path
 * @param {Array} permissions - Array of permission objects from roleInfo.permissions
 * @param {string} path - Navigation path (e.g., "/courses", "/cultural-module")
 * @param {string} userType - Type of the user
 * @returns {boolean} True if user has update permission, false otherwise
 */
export const canUpdate = (permissions, path, userType) => {
  if (userType === "admin" || userType === "super-admin") return true;
  const permission = getPermissionByPath(permissions, path);
  return permission?.update === true;
};

/**
 * Checks if user has remove/delete permission for a specific route/path
 * @param {Array} permissions - Array of permission objects from roleInfo.permissions
 * @param {string} path - Navigation path (e.g., "/courses", "/cultural-module")
 * @param {string} userType - Type of the user
 * @returns {boolean} True if user has remove permission, false otherwise
 */
export const canRemove = (permissions, path, userType) => {
  if (userType === "admin" || userType === "super-admin") return true;
  const permission = getPermissionByPath(permissions, path);
  return permission?.remove === true;
};
